import { useCallback, useEffect, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { toast } from '../../../../../../../ui/toast';
import {
  addQuestionToAssessment,
  deleteAllQuestionFromAssessment,
  deleteQuestionFromAssessment,
  getAssessmentQuestions,
  getAssessmentTemplate,
} from '../../../../../../../utils/api_service/assessment_api';
import {
  addQuestion,
  updateQuestion,
} from '../../../../../../../utils/api_service/job_api';
import {
  convertSecondsToTimeTaken,
  convertStringToUnix,
} from '../../../../../../../utils/helpers/time';
import { editQuestionRedux } from '../../../../../../../utils/redux_store/features/question_reducer';
import {
  Question,
  QuestionInfoTotalCount,
  SimplifiedTemplate,
} from '../../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';

export const useQuestions = (
  assessmentId: string,
  completedElement: () => void,
) => {
  // — the list you drag/drop on the main page
  const [questionList, setQuestionList] = useState<Question[]>([]);
  // — the “already selected” questions
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);

  // — DnD + expand/collapse + drawer open state
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [isOpenSelectQuestions, setIsOpenSelectQuestions] = useState(false);
  const [isOpenQuickBuild, setIsOpenQuickBuild] = useState(false);
  // — header totals
  const [totalCount, setTotalCount] = useState<QuestionInfoTotalCount>({
    totalPoints: 0,
    totalTime: '00:00',
  });

  // 1️⃣ Fetch once, seed both lists
  const fetchQuestions = useCallback(() => {
    getAssessmentQuestions(assessmentId).then((response) => {
      setQuestionList(response);
      setSelectedQuestions(response); // ← seed drawer’s initialSelected

      const totalPoints = response.reduce(
        (sum: number, q: Question) => sum + q.points,
        0,
      );
      const totalSeconds = response.reduce(
        (sum: number, q: Question) => sum + q.time,
        0,
      );
      setTotalCount({
        totalPoints,
        totalTime: convertSecondsToTimeTaken(totalSeconds) || '00:00',
      });
    });
  }, [assessmentId]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // 2️⃣ DnD handlers
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const copy = Array.from(questionList);
    const [moved] = copy.splice(result.source.index, 1);
    copy.splice(result.destination.index, 0, moved);
    setQuestionList(copy);
  };

  // 3️⃣ Expand / Collapse
  const toggleExpand = (id: string) =>
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const handleExpandAll = () =>
    setExpandedIds((prev) =>
      prev.length === questionList.length ? [] : questionList.map((q) => q.id),
    );

  // 4️⃣ Drawer open/close
  const handleOpenSelectQuestions = () => {
    setSelectedQuestions(questionList); // re-seed in case questionList changed
    setIsOpenSelectQuestions(true);
  };
  const handleOpenQuickBuild = () => {
    setIsOpenQuickBuild(true);
  };
  const handleCloseQuickBuild = () => setIsOpenQuickBuild(false);
  const handleCloseSelectQuestions = () => setIsOpenSelectQuestions(false);

  // 5️⃣ On Save, grab the final array and close
  const handleSaveSelection = (finalSelection: Question[]) => {
    setSelectedQuestions(finalSelection);
    setIsOpenSelectQuestions(false);
    // TODO: persist to backend if needed
  };
  const handleAddQuestion = (q: Question) => {
    addQuestionToAssessment(
      questionList.length.toString(),
      q.id,
      assessmentId,
    ).then(() => {
      fetchQuestions();
    });
  };
  const dispatch = useDispatch();

  const editQuestion = (question: Question) => {
    updateQuestion(question).then((response) => {
      if (response.id) {
        let questionListCopy = questionList;
        let index = questionListCopy.findIndex(
          (item) => item.id === question.id,
        );
        let editedQuestionPrevState = questionListCopy[index];
        questionListCopy[index] = question;
        setQuestionList(questionListCopy);
        let convertedPrevious = convertStringToUnix(totalCount.totalTime) || 0;
        let timeUnixCalc =
          convertedPrevious - editedQuestionPrevState.time + question.time;
        let convertedTime = convertSecondsToTimeTaken(timeUnixCalc);
        let points: string = question.points.toString();
        setTotalCount({
          totalPoints:
            totalCount.totalPoints -
            editedQuestionPrevState.points +
            parseInt(points),
          totalTime: convertedTime !== undefined ? convertedTime : '00:00',
        });
        dispatch(editQuestionRedux(question));
      }
    });
  };

  const deleteQuestion = async (id: string) => {
    const response = await deleteQuestionFromAssessment(id);
    if (response.ok) {
      setQuestionList((prev) => {
        const updatedList = prev.filter(
          (item) => item.assessmentQuestionId !== id,
        );
        return updatedList;
      });

      const deletedQuestion = questionList.find(
        (q) => q.assessmentQuestionId === id,
      );
      if (deletedQuestion) {
        const convertedPrevious =
          convertStringToUnix(totalCount.totalTime) || 0;
        const timeUnixCalc = convertedPrevious - deletedQuestion.time;
        const convertedTime = convertSecondsToTimeTaken(timeUnixCalc);
        setTotalCount({
          totalPoints: totalCount.totalPoints - deletedQuestion.points,
          totalTime: convertedTime ?? '00:00',
        });
      }
    }
  };
  useEffect(() => {
    if (questionList.length > 0) {
      if (completedElement) {
        completedElement();
      }
    }
  }, [questionList]);

  const [editMap, setEditMap] = useState<Record<string, boolean>>({});

  const [isTemplateDrawerOpen, setIsTemplateDrawerOpen] = useState(false);
  const [isTemplateCreateOpen, setIsTemplateCreateOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const setDisabled = (id: string, disabled: boolean) =>
    setEditMap((m) => ({ ...m, [id]: disabled }));

  const handleTemplateSave = (templateData: any) => {
    console.log('Template saved:', templateData);
    // TODO: Implement API call to save template

    // Show success message
    const action = templateData.id ? 'updated' : 'created';
    toast.success(
      `Assessment template "${templateData.name}" ${action} successfully!`,
      {
        position: 'bottom-center',
      },
    );

    setIsTemplateCreateOpen(false);
  };

  const questionTypes: SelectOptions[] = [
    { value: 'Modern', label: 'Video' },
    { value: 'Classic', label: 'Text' },
  ];
  // const [totalCount, setTotalCount] = useState<QuestionInfoTotalCount>({
  //   totalPoints: 0,
  //   totalTime: '00:00',
  // });
  const [addQuestionModalOpen, setAddQuestionModalOpen] =
    useState<boolean>(false);
  const handleChange = (question: Question) => {
    addQuestionToAssessment(
      questionList.length.toString(),
      question.id,
      assessmentId,
    ).then(() => {
      fetchQuestions();
    });
  };
  const addNewQuestion = (question: Question) => {
    addQuestion(question)
      .then((response) => {
        if (response && response.id) {
          handleChange(response);
          setAddQuestionModalOpen(false);
        } else {
          const errorMsg =
            response?.error?.error ||
            response?.error ||
            (response?.status === false ? 'Validation Error' : 'Unknown Error');
          toast.error(`Error: ${errorMsg}`, { position: 'bottom-center' });
        }
      })
      .catch((error) => {
        console.error('Error adding question:', error);
        toast.error('Failed to add question. Please try again.', {
          position: 'bottom-center',
        });
      });
  };
  const toggleAddQuestionModal = () => {
    setAddQuestionModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDeleteTemplate = async () => {
    const deleteIds = questionList
      .map((item) => item.assessmentQuestionId)
      .filter((id): id is string => Boolean(id));

    if (deleteIds.length > 0) {
      try {
        const response = await deleteAllQuestionFromAssessment(deleteIds);
        if (response.ok) {
          setQuestionList([]);
          setIsModalOpen(false);
          toast.success('All Questions have been deleted successfully!', {
            position: 'bottom-center',
          });
        }
      } catch (e) {
        toast.error('Error Deleting Questions', { position: 'bottom-center' });
      }
    }
  };

  const handleTemplateSelect = async (template: SimplifiedTemplate) => {
    try {
      const response = await getAssessmentTemplate(template.templateId);

      if (response.ok) {
        const responseData = await response.json();
        const deleteIds = questionList
          .map((item) => item.assessmentQuestionId)
          .filter((id): id is string => Boolean(id));
        if (questionList.length !== 0) {
          await deleteAllQuestionFromAssessment(deleteIds);
        }
        setQuestionList([]);
        if (responseData.questionAnswer?.length) {
          // Run addNewQuestion on each question sequentially or in parallel
          await Promise.all(
            responseData.questionAnswer.map((question: Question) =>
              addNewQuestion(question),
            ),
          );
        }
        setIsTemplateDrawerOpen(false);
      }
    } catch (error) {
      toast.error('Error Loading Template', { position: 'bottom-center' });
    }
  };

  return {
    expandedIds,
    questionList,
    handleExpandAll,
    handleOpenSelectQuestions,
    handleDragEnd,
    toggleExpand,
    isOpenSelectQuestions,
    handleCloseSelectQuestions,
    isOpenQuickBuild,
    handleOpenQuickBuild,
    handleCloseQuickBuild,
    handleAddQuestion,
    selectedQuestions,
    handleSaveSelection,
    editQuestion,
    deleteQuestion,
    setQuestionList,
    totalCount,
    setTotalCount,
    setIsTemplateDrawerOpen,
    setIsTemplateCreateOpen,
    setIsModalOpen,
    toggleAddQuestionModal,
    setDisabled,
    editMap,
    questionTypes,
    isModalOpen,
    handleCloseModal,
    handleDeleteTemplate,
    addQuestionModalOpen,
    addNewQuestion,
    setAddQuestionModalOpen,
    isTemplateDrawerOpen,
    handleTemplateSelect,
    isTemplateCreateOpen,
    handleTemplateSave,
    fetchQuestions,
  };
};
